import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/AuthContext'
import { loginSchema, otpSchema, type LoginInput, type OTPInput } from '@/lib/validations/auth'
import { getAuthErrorMessage } from '@/lib/utils/errors'
import { Shield } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export function LoginPage() {
  const navigate = useNavigate()
  const { signIn, verifyMFAChallenge } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [showMFAInput, setShowMFAInput] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    formState: { errors: otpErrors },
  } = useForm<OTPInput>({
    resolver: zodResolver(otpSchema),
  })

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true)
    try {
      await signIn(data.email, data.password)

      // Check if MFA is required
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (aal && aal.nextLevel === 'aal2' && aal.currentLevel !== aal.nextLevel) {
        setCredentials({ email: data.email, password: data.password })
        setShowMFAInput(true)
        toast.info('Please enter your 2FA code')
        setIsLoading(false)
        return
      }

      // Check if there's a pending invitation to accept
      const pendingToken = sessionStorage.getItem('pending_invitation_token')
      if (pendingToken) {
        navigate(`/invitations/${pendingToken}`)
      } else {
        navigate('/dashboard')
      }
      toast.success('Successfully logged in!')
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const onMFASubmit = async (data: OTPInput) => {
    if (!credentials) return

    setIsLoading(true)
    try {
      await verifyMFAChallenge(data.code)

      // Check if there's a pending invitation to accept
      const pendingToken = sessionStorage.getItem('pending_invitation_token')
      if (pendingToken) {
        navigate(`/invitations/${pendingToken}`)
      } else {
        navigate('/dashboard')
      }
      toast.success('Successfully logged in!')
    } catch (error) {
      toast.error('Invalid 2FA code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout
      title={showMFAInput ? "Two-Factor Authentication" : "Welcome Back"}
      description={showMFAInput ? "Enter the 6-digit code from your authenticator app" : "Sign in to your AionTrack account"}
    >
      {showMFAInput ? (
        <form onSubmit={handleSubmitOTP(onMFASubmit)} className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
          </div>

          <div>
            <label htmlFor="code" className="block text-sm font-medium mb-2">
              Authentication Code
            </label>
            <Input
              id="code"
              type="text"
              placeholder="000000"
              maxLength={6}
              {...registerOTP('code')}
              disabled={isLoading}
              className="font-mono text-center text-lg tracking-widest"
              autoFocus
            />
            {otpErrors.code && (
              <p className="text-sm text-destructive mt-1">{otpErrors.code.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>

          <div className="text-center text-sm">
            <button
              type="button"
              onClick={() => {
                setShowMFAInput(false)
                setCredentials(null)
              }}
              className="text-primary hover:underline"
            >
              Back to login
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              disabled={isLoading}
              aria-invalid={errors.email ? 'true' : 'false'}
            />
            {errors.email && (
              <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              Password
            </label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              disabled={isLoading}
              aria-invalid={errors.password ? 'true' : 'false'}
            />
            {errors.password && (
              <p className="text-sm text-destructive mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/signup" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  )
}
